/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef} from 'react';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {useMedia} from '../../hooks/useMedia';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {MenuItem, MenuItems} from '../../types/components';
import {PrimaryText} from './PrimaryText';
import navStyles from './Navigation.module.scss';

// defines the styles used by secondary navigation item
export const useStyles = makeStyles(theme => ({
  listItem : {
    '&:hover' : {
      color                    : `${dlsColor('arcticBlue')} !important`,
      backgroundColor          : 'transparent',
      '& span[class*=enabled]' : {
        color : `${dlsColor('lightBlue')} !important`
      }
    }
  },
  listItemText : {
    ...dlsAlt('maps', 'layer', 'tabHeader'),
    lineHeight : '4px',
    width      : '100%',
    minWidth   : '190px',
    '& span'   : {
      color : `${dlsColor('aaBlue')} !important`
    }
  },
  inactiveNestedListItemText : {
    '& span' : {
      color : `${dlsColor('aaBlue')} !important`
    }
  },
  activeNestedListItemText : {
    '& span' : {
      color : `${dlsColor('lightBlue')} !important`
    }
  },
  itemSecondaryText : {
    fontSize : '10px !important'
  }
}));

// defines the props for secondary navigation item
export interface SecondaryNavigationItemTypes {
  nestedMenuItems?: MenuItems;
  // [DLS PACKAGE TODO]: Compact arguments to typed parameters object
  onSetPage(page: string, index: number, isSecondaryIndex?: boolean, item?: MenuItem): void;
  currentPage: string;
  item: MenuItem;
  index: number;
  handleTooltipClick: () => void;
  toggleToolTip: (index: number) => void;
}

/**
 * @SecondaryNavigationItem
 *
 * Defines the individual secondary navigation item. This component should be rendered only
 * from SecondaryNavigationItems component with all the props mentioned below.
 *
 * ```typescript
 *
 *   <SecondaryNavigationItem item={item}
 *     index={index}
 *     handleTooltipClick={handleTooltipClick}
 *     onSetPage={onSetPage} />
 *
 * ```
 */
export const SecondaryNavigationItem = forwardRef(({
  item,
  index,
  onSetPage,
  currentPage,
  handleTooltipClick,
  toggleToolTip
}: SecondaryNavigationItemTypes, ref: any) => {
  const classes     = useStyles();
  const {isDesktop} = useMedia();
  const {
    label,
    isPreview,
    page,
    meta
  } = item;
  const selected     = page === currentPage;
  const getClassName = (label = '') =>
    navStyles[`${selected ? 'active' : 'inactive'}NestedListItem${label}`];

  return (
    <ListItem button
      ref={ref}
      className={classnames(
        classes.listItem,
        getClassName()
      )}
      key={label}
      disableTouchRipple
      selected={selected}
      onMouseLeave={function(e) {
        isPreview
          ? e.preventDefault()
          : isDesktop ? handleTooltipClick() : e.preventDefault();
      }}
      onClick={function(e) {
        e.stopPropagation();
        if (isPreview && (page !== currentPage)) {
          // [DLS PACKAGE TODO]: Refactor to parameters object
          onSetPage(page, index, true, item);
        } else {
          toggleToolTip(index);
        }
      }}>
      <ListItemText className={getClassName('Text')}
        classes={{
          primary : classnames(
            classes.listItemText,
            classes.activeNestedListItemText,
            classes.inactiveNestedListItemText,
            navStyles[`${!isPreview ? 'disabled' : 'enabled'}ListItemText`]),
          secondary : classnames(navStyles.itemSecondaryText, classes.itemSecondaryText)
        }} primary={<PrimaryText label={label} tag={meta?.tag} />}
        secondary={translate(meta?.text)} />
    </ListItem>
  );
});

export default SecondaryNavigationItem;
